// Modules
import { observable, action, autorun } from 'mobx';
import firebase from 'firebase';
import 'firebase/firestore';

// Config
import { firebaseConfig } from '../data/config';

class AuthStore {
  constructor() {
    firebase.initializeApp(firebaseConfig);
    this.authUnsubscriber = firebase.auth().onAuthStateChanged((user) => {
      console.log('Auth State Changed');
      if (user) {
        console.log('Logged In');
        this.loading = false;
        this.currentUser = user;
      } else {
        console.log('Not Logged In');
        this.loading = false;
        this.currentUser = null;
      }
    });
  }

  @observable loading = true;
  @observable initializing = false;
  @observable currentUser = null;
  @observable email = '';
  @observable password = '';
  @observable username = '';
  @observable errorMessage = '';
  @observable notifyMessage = '';

  @action reset = () => {
    this.email = '';
    this.password = '';
    this.username = '';
  }

  @action unsubscribe = () => {
    if (this.authUnsubscriber) {
      this.authUnsubscriber();
    }
  }

  @action isValidEmail = () => {
    if (this.email) {
      const re = /^(([^<>()[\].,;:\s@']+(.[^<>()[\].,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}\])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (re.test(this.email)) {
        return true;
      }
      this.showAuthError('Please enter a valid email address.');
    } else {
      this.showAuthError('Email address is required.');
    }
    return false;
  }

  @action isValidPassword = () => {
    if (this.password) {
      if (this.password.length >= 6) {
        return true;
      }
      this.showAuthError('Password must be at least 6 characters long.');
    } else {
      this.showAuthError('Password is required.');
    }
    return false;
  }

  @action isValidUsername = () => {
    if (this.username) {
      const re = new RegExp('^[a-zA-Z0-9]{5,16}$');
      if (re.test(this.username)) {
        return true;
      }
      this.showAuthError('Usernames must be between 5 and 16 characters long, letters and numbers only, and no spaces.');
    } else {
      this.showAuthError('Username is required.');
    }
    return false;
  }

  @action isUsernameAvailable() {
    return new Promise((resolve, reject) => {
      const db = firebase.firestore();
      const usernameRef = db.collection('usernames').doc(this.username);
      usernameRef
        .get()
        .then((userDoc) => {
          if (userDoc.exists) {
            this.showAuthError('Sorry, but that username is not available');
            reject();
          } else {
            resolve();
          }
        }).catch((error) => {
          this.handleFirebaseErrors(error);
          reject();
        });
    });
  }

  @action handleFirebaseErrors = (error) => {
    switch (error.code) {
      case 'auth/user-not-found':
        this.showAuthError('A user with that email address doesn\'t exist.');
        break;
      case 'auth/wrong-password':
        this.showAuthError('Password is incorrect.');
        break;
      case 'auth/email-already-in-use':
        this.showAuthError('A user with that email address already exists.');
        break;
      case 'auth/invalid-email':
        this.showAuthError('Please enter a valid email address.');
        break;
      case 'auth/account-exists-with-different-credential':
        this.showAuthError('An account with that email exists with another provider. If you used Facebook try to login with Google or vice versa.');
        break;
      default:
    }
  }

  @action showAuthError = (message) => {
    this.errorMessage = '';
    this.notifyMessage = '';
    this.errorMessage = message;
  }

  @action showAuthNotification = (message) => {
    this.errorMessage = '';
    this.notifyMessage = '';
    this.notifyMessage = message;
  }

  @action updateUserProfile(userId) {
    return new Promise((resolve) => {
      const db = firebase.firestore();
      const userRef = db.collection('users').doc(userId);
      userRef.get().then((userDoc) => {
        if (userDoc.exists) {
          userRef.update({
            lastLoggedIn: firebase.firestore.FieldValue.serverTimestamp(),
          }).catch((error) => {
            this.handleFirebaseErrors(error);
          });
        } else {
          userRef.set({
            createdOn: firebase.firestore.FieldValue.serverTimestamp(),
            lastLoggedIn: firebase.firestore.FieldValue.serverTimestamp(),
          }).then(() => {
            resolve();
          }).catch((error) => {
            this.handleFirebaseErrors(error);
          });
        }
      });
    });
  }

  @action setUsername(userId, username) {
    return new Promise((resolve) => {
      const db = firebase.firestore();
      const userRef = db.collection('users').doc(userId);
      const usernameRef = db.collection('usernames').doc(username);
      userRef.update({
        username,
      }).then(() => {
        usernameRef.set({
          createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        });
        resolve();
      }).catch((error) => {
        this.handleFirebaseErrors(error);
      });
    });
  }

  @action createUserWithEmailAndPassword() {
    return new Promise((resolve) => {
      if (this.isValidEmail() && this.isValidPassword()) {
        firebase.auth().createUserWithEmailAndPassword(this.email, this.password).then((user) => {
          this.updateUserProfile(user.uid).then(() => {
            resolve();
          });
        }).catch((error) => {
          this.handleFirebaseErrors(error);
        });
      }
    });
  }

  @action signInWithEmailAndPassword() {
    return new Promise((resolve) => {
      if (this.isValidEmail() && this.isValidPassword()) {
        firebase.auth().signInWithEmailAndPassword(this.email, this.password).then((user) => {
          this.updateUserProfile(user.uid).then(() => {
            resolve();
          });
        }).catch((error) => {
          this.handleFirebaseErrors(error);
        });
      }
    });
  }

  @action sendPasswordResetEmail() {
    return new Promise((resolve) => {
      if (this.isValidEmail()) {
        firebase.auth().sendPasswordResetEmail(this.email).then(() => {
          this.showAuthNotification('An email has been sent with instructions for resetting your password.');
          resolve();
        }).catch((error) => {
          this.handleFirebaseErrors(error);
        });
      }
    });
  }

  @action signOut = () => {
    firebase.auth().signOut();
  }

  // Debug
  debug = autorun(() => {
    // console.log(this.email);
  });
}

const singleton = new AuthStore();

export default singleton;



// WEBPACK FOOTER //
// ./src/stores/AuthStore.js